import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { 
  Camera, 
  Video, 
  Square, 
  Maximize2,
  Volume2,
  VolumeX,
  Monitor,
  AlertCircle,
  Wifi,
  WifiOff,
  ScanLine
} from "lucide-react";

interface CameraFeed {
  id: string;
  name: string;
  location: string;
  status: 'online' | 'offline' | 'recording';
  streamUrl: string;
  imageUrl?: string; // Base64 image data or URL
  type: 'fixed' | 'ptz'; // Pan-Tilt-Zoom
  category: 'general' | 'anpr' | 'facial'; // Camera specialization
  resolution: string;
  lastUpdate: Date;
  plateNumber?: string; // For ANPR cameras
  direction?: 'entry' | 'exit'; // For ANPR cameras
}

interface CCTVMonitorProps {
  selectedCamera?: string;
  onCameraSelect?: (cameraId: string) => void;
  compact?: boolean;
  viewMode?: 'single' | 'grid';
  cameraFilter?: 'all' | 'general' | 'anpr' | 'facial';
  onViewModeChange?: (mode: 'single' | 'grid') => void;
  cameras?: any[]; // Real camera data from API
}

export function CCTVMonitor({ 
  selectedCamera, 
  onCameraSelect, 
  compact = false, 
  viewMode = 'single',
  cameraFilter = 'all',
  onViewModeChange,
  cameras: propCameras
}: CCTVMonitorProps) {
  // Use real camera data if provided, otherwise fall back to demo data
  const [demoCameras] = useState<CameraFeed[]>([
    // General Surveillance Cameras (16 cameras)
    { id: 'gen-01', name: 'Main Gate Entrance', location: 'Main Gate', status: 'online', streamUrl: 'demo-stream-1', type: 'ptz', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-02', name: 'Scale Platform 1', location: 'Weighbridge 1', status: 'recording', streamUrl: 'demo-stream-2', type: 'fixed', category: 'general', resolution: '4K', lastUpdate: new Date() },
    { id: 'gen-03', name: 'Scale Platform 2', location: 'Weighbridge 2', status: 'online', streamUrl: 'demo-stream-3', type: 'fixed', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-04', name: 'Parking Overview North', location: 'North Parking', status: 'online', streamUrl: 'demo-stream-4', type: 'ptz', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-05', name: 'Parking Overview South', location: 'South Parking', status: 'recording', streamUrl: 'demo-stream-5', type: 'ptz', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-06', name: 'Perimeter East', location: 'East Fence', status: 'online', streamUrl: 'demo-stream-6', type: 'fixed', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-07', name: 'Perimeter West', location: 'West Fence', status: 'online', streamUrl: 'demo-stream-7', type: 'fixed', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-08', name: 'Loading Bay 1', location: 'Loading Area 1', status: 'online', streamUrl: 'demo-stream-8', type: 'ptz', category: 'general', resolution: '4K', lastUpdate: new Date() },
    { id: 'gen-09', name: 'Loading Bay 2', location: 'Loading Area 2', status: 'recording', streamUrl: 'demo-stream-9', type: 'ptz', category: 'general', resolution: '4K', lastUpdate: new Date() },
    { id: 'gen-10', name: 'Admin Building', location: 'Office Complex', status: 'online', streamUrl: 'demo-stream-10', type: 'fixed', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-11', name: 'Maintenance Area', location: 'Workshop', status: 'online', streamUrl: 'demo-stream-11', type: 'fixed', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-12', name: 'Fuel Station', location: 'Fuel Depot', status: 'online', streamUrl: 'demo-stream-12', type: 'ptz', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-13', name: 'Guard House', location: 'Security Post', status: 'online', streamUrl: 'demo-stream-13', type: 'fixed', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    { id: 'gen-14', name: 'Storage Yard', location: 'Container Storage', status: 'online', streamUrl: 'demo-stream-14', type: 'ptz', category: 'general', resolution: '4K', lastUpdate: new Date() },
    { id: 'gen-15', name: 'Emergency Exit', location: 'Safety Gate', status: 'offline', streamUrl: 'demo-stream-15', type: 'fixed', category: 'general', resolution: '1080p', lastUpdate: new Date(Date.now() - 600000) },
    { id: 'gen-16', name: 'Visitor Parking', location: 'Guest Area', status: 'online', streamUrl: 'demo-stream-16', type: 'ptz', category: 'general', resolution: '1080p', lastUpdate: new Date() },
    
    // ANPR Cameras (10 cameras)
    { id: 'anpr-01', name: 'Entry Gate ANPR', location: 'Main Entrance', status: 'online', streamUrl: 'demo-stream-17', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-02', name: 'Exit Gate ANPR', location: 'Exit Point', status: 'offline', streamUrl: 'demo-stream-18', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date(Date.now() - 300000) },
    { id: 'anpr-03', name: 'Scale Approach ANPR', location: 'Scale Entry', status: 'recording', streamUrl: 'demo-stream-19', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-04', name: 'Scale Exit ANPR', location: 'Scale Exit', status: 'online', streamUrl: 'demo-stream-20', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-05', name: 'Parking Entry ANPR', location: 'Parking Gate', status: 'online', streamUrl: 'demo-stream-21', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-06', name: 'Loading Bay ANPR', location: 'Loading Zone', status: 'recording', streamUrl: 'demo-stream-22', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-07', name: 'Service Gate ANPR', location: 'Service Entrance', status: 'online', streamUrl: 'demo-stream-23', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-08', name: 'Emergency Gate ANPR', location: 'Emergency Exit', status: 'online', streamUrl: 'demo-stream-24', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-09', name: 'Checkpoint ANPR', location: 'Security Check', status: 'recording', streamUrl: 'demo-stream-25', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    { id: 'anpr-10', name: 'Perimeter ANPR', location: 'Outer Gate', status: 'online', streamUrl: 'demo-stream-26', type: 'fixed', category: 'anpr', resolution: '4K', lastUpdate: new Date() },
    
    // Facial Recognition Cameras (6 cameras)
    { id: 'face-01', name: 'Driver Face Recognition', location: 'Driver Station', status: 'recording', streamUrl: 'demo-stream-27', type: 'fixed', category: 'facial', resolution: '4K', lastUpdate: new Date() },
    { id: 'face-02', name: 'Security Checkpoint Face', location: 'Security Booth', status: 'online', streamUrl: 'demo-stream-28', type: 'ptz', category: 'facial', resolution: '4K', lastUpdate: new Date() },
    { id: 'face-03', name: 'Admin Entry Face', location: 'Admin Building', status: 'online', streamUrl: 'demo-stream-29', type: 'fixed', category: 'facial', resolution: '1080p', lastUpdate: new Date() },
    { id: 'face-04', name: 'Visitor Face Check', location: 'Reception', status: 'online', streamUrl: 'demo-stream-30', type: 'fixed', category: 'facial', resolution: '4K', lastUpdate: new Date() },
    { id: 'face-05', name: 'Scale Operator Face', location: 'Scale Control', status: 'recording', streamUrl: 'demo-stream-31', type: 'fixed', category: 'facial', resolution: '1080p', lastUpdate: new Date() },
    { id: 'face-06', name: 'Control Room Face', location: 'CCTV Control', status: 'online', streamUrl: 'demo-stream-32', type: 'fixed', category: 'facial', resolution: '4K', lastUpdate: new Date() }
  ]);

  // Convert real camera data to CameraFeed format
  const realCameras: CameraFeed[] = propCameras ? propCameras.map((cam: any) => ({
    id: cam.id,
    name: cam.name,
    location: cam.location,
    status: cam.isActive ? 'online' : 'offline',
    streamUrl: cam.imageUrl || '',
    imageUrl: cam.imageUrl || '', // Add imageUrl for display
    type: 'fixed' as const,
    category: cam.category || 'general' as const,
    resolution: '1080p',
    lastUpdate: new Date(cam.lastUpdate || Date.now())
  })) : [];

  // Use real cameras if available, otherwise demo cameras
  const allCameras = realCameras.length > 0 ? realCameras : demoCameras;

  // Filter cameras based on category
  const filteredCameras = cameraFilter === 'all' 
    ? allCameras 
    : allCameras.filter(cam => cam.category === cameraFilter);

  const [activeCamera, setActiveCamera] = useState(selectedCamera || filteredCameras[0]?.id);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (selectedCamera) {
      setActiveCamera(selectedCamera);
    }
  }, [selectedCamera]);

  const currentCamera = filteredCameras.find(cam => cam.id === activeCamera);
  
  // Debug logging
  React.useEffect(() => {
    console.log('🎥 All cameras:', filteredCameras.length);
    console.log('🎥 Active camera ID:', activeCamera);
    console.log('🎥 Current camera:', currentCamera?.name);
    console.log('🎥 Has imageUrl:', !!currentCamera?.imageUrl);
    if (currentCamera?.imageUrl) {
      console.log('🎥 ImageUrl length:', currentCamera.imageUrl.length);
      console.log('🎥 ImageUrl preview:', currentCamera.imageUrl.substring(0, 100));
    }
  }, [filteredCameras, activeCamera, currentCamera]);

  const handleCameraChange = (cameraId: string) => {
    setActiveCamera(cameraId);
    onCameraSelect?.(cameraId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'recording': return 'bg-red-500 animate-pulse';
      case 'offline': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <Wifi className="w-3 h-3" />;
      case 'recording': return <Video className="w-3 h-3" />;
      case 'offline': return <WifiOff className="w-3 h-3" />;
      default: return <AlertCircle className="w-3 h-3" />;
    }
  };

  if (compact) {
    return (
      <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-sm">
            <Camera className="w-4 h-4 mr-2" />
            CCTV Monitor
            <Badge variant="secondary" className="ml-auto">
              {filteredCameras.filter(c => c.status === 'online' || c.status === 'recording').length}/{filteredCameras.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={activeCamera} onValueChange={handleCameraChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filteredCameras.map(camera => (
                <SelectItem key={camera.id} value={camera.id}>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(camera.status)}`} />
                    {camera.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {currentCamera && (
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: '70vh' }}>
              {currentCamera.imageUrl ? (
                <>
                  <img 
                    src={currentCamera.imageUrl} 
                    alt={currentCamera.name}
                    className="w-full h-full object-cover"
                    onLoad={() => console.log(`✅ Image loaded: ${currentCamera.name}`)}
                    onError={(e) => {
                      console.log(`❌ Image failed: ${currentCamera.name}`, e);
                      console.log(`❌ Image URL length: ${currentCamera.imageUrl?.length}`);
                      console.log(`❌ Image URL preview: ${currentCamera.imageUrl?.substring(0, 100)}`);
                    }}
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  
                  {/* ANPR Plate Info Overlay */}
                  {currentCamera.plateNumber && (
                    <div className="absolute top-4 left-4 bg-black/80 backdrop-blur-sm px-4 py-3 rounded-lg border-2 border-green-500">
                      <div className="flex items-center gap-3">
                        <ScanLine className="w-5 h-5 text-green-400" />
                        <div>
                          <div className="text-white font-mono text-lg font-bold">
                            {currentCamera.plateNumber}
                          </div>
                          <div className="text-green-400 text-xs uppercase font-semibold">
                            {currentCamera.direction === 'entry' ? '↓ ENTRY' : '↑ EXIT'}
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-300 text-xs mt-2">
                        {new Date(currentCamera.lastUpdate).toLocaleString()}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full bg-gray-900">
                  <div className="text-center text-white">
                    <Camera className="w-8 h-8 mx-auto mb-2 opacity-60" />
                    <div className="text-sm">{currentCamera.name}</div>
                  </div>
                </div>
              )}
              

              <div className="absolute top-2 right-2">
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="h-8 w-8 p-0 text-white hover:bg-white/20"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-200 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-slate-600 to-slate-700 text-white">
        <CardTitle className="flex items-center">
          <div className="p-2 bg-white/20 rounded-lg mr-3">
            <Monitor className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xl font-bold">CCTV Monitoring</div>
            <div className="text-slate-200 text-sm">Live camera feeds and security monitoring</div>
          </div>
          <div className="ml-auto flex items-center space-x-3">
            {onViewModeChange && (
              <div className="flex bg-white/20 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={viewMode === 'single' ? 'secondary' : 'ghost'}
                  className="h-7 px-3 text-xs text-white"
                  onClick={() => onViewModeChange('single')}
                >
                  Single
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  className="h-7 px-3 text-xs text-white"
                  onClick={() => onViewModeChange('grid')}
                >
                  Grid
                </Button>
              </div>
            )}
            <Badge variant="secondary" className="bg-white text-slate-700 font-bold">
              {filteredCameras.filter(c => c.status === 'online' || c.status === 'recording').length}/{filteredCameras.length} Online
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-0">
        {viewMode === 'grid' ? (
          /* Grid View - Clean camera grid with text labels below */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 p-6 min-h-[600px] max-h-[800px] overflow-y-auto">
            {filteredCameras.map(camera => (
              <div key={camera.id} className="flex flex-col space-y-2">
                <button
                  onClick={() => {
                    handleCameraChange(camera.id);
                    onViewModeChange?.('single');
                  }}
                  className="relative bg-black rounded-lg aspect-video overflow-hidden border-2 border-gray-700 hover:border-blue-400 transition-all duration-200 cursor-pointer group w-full"
                  data-testid={`grid-camera-${camera.id}`}
                >
                  {camera.imageUrl ? (
                    <>
                      <img 
                        src={camera.imageUrl} 
                        alt={camera.name}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* ANPR Plate Overlay on Grid */}
                      {camera.plateNumber && (
                        <div className="absolute bottom-2 left-2 right-2 bg-black/80 backdrop-blur-sm px-2 py-1 rounded border border-green-500">
                          <div className="text-white font-mono text-xs font-bold text-center">
                            {camera.plateNumber}
                          </div>
                          <div className="text-green-400 text-[10px] uppercase font-semibold text-center">
                            {camera.direction === 'entry' ? 'ENTRY ↓' : 'EXIT ↑'}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full bg-gray-900">
                      <Camera className="w-8 h-8 opacity-60 text-white" />
                    </div>
                  )}
                  
                  <div className="absolute top-2 left-2 z-10 pointer-events-none">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(camera.status)}`} />
                  </div>

                  <div className="absolute inset-0 bg-black/0 hover:bg-blue-600/20 transition-all duration-200 flex items-center justify-center pointer-events-none">
                    <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                </button>
                
                {/* Camera label below the feed */}
                <div className="text-center">
                  <div className="bg-gray-800 text-white px-3 py-2 rounded-md">
                    <div className="font-medium text-sm truncate">{camera.name}</div>
                    <div className="text-xs opacity-75 truncate">
                      {camera.plateNumber ? (
                        <>
                          {camera.location} • {new Date(camera.lastUpdate).toLocaleTimeString()}
                        </>
                      ) : (
                        camera.location
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Single Camera View */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-[600px]">
            {/* Camera Selection Sidebar - Optimized for 32 cameras */}
            <div className="bg-gray-50 border-r p-4 flex flex-col">
              <h3 className="font-semibold text-gray-900 mb-4">Camera Feeds</h3>
              <div className="text-xs text-gray-600 mb-3">
                {filteredCameras.filter(c => c.status === 'online' || c.status === 'recording').length} of {filteredCameras.length} cameras online
              </div>
              <div className="flex-1 overflow-y-auto space-y-2 max-h-[500px]">
                {filteredCameras.map(camera => (
                  <button
                    key={camera.id}
                    onClick={() => handleCameraChange(camera.id)}
                    className={`w-full p-2 rounded-md text-left transition-all ${
                      activeCamera === camera.id 
                        ? 'bg-slate-600 text-white shadow-md' 
                        : 'bg-white hover:bg-gray-100 border'
                    }`}
                    data-testid={`camera-${camera.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(camera.status)}`} />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-xs truncate">{camera.name}</div>
                          <div className="text-xs opacity-75 truncate">
                            {camera.plateNumber ? (
                              <span className="font-mono">{camera.plateNumber}</span>
                            ) : (
                              camera.location
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {camera.plateNumber ? <ScanLine className="w-3 h-3" /> : getStatusIcon(camera.status)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

          <div className="lg:col-span-2 bg-black relative">
            {currentCamera && (
              <>
                <div className="relative bg-black overflow-hidden" style={{ height: '70vh' }}>
                  {currentCamera.imageUrl ? (
                    <>
                      <img 
                        src={currentCamera.imageUrl} 
                        alt={currentCamera.name}
                        className="w-full h-full object-cover"
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                      
                      {/* ANPR Plate Info Overlay */}
                      {currentCamera.plateNumber && (
                        <div className="absolute top-4 left-4 bg-black/80 backdrop-blur-sm px-6 py-4 rounded-lg border-2 border-green-500">
                          <div className="flex items-center gap-4">
                            <ScanLine className="w-6 h-6 text-green-400" />
                            <div>
                              <div className="text-white font-mono text-2xl font-bold">
                                {currentCamera.plateNumber}
                              </div>
                              <div className="text-green-400 text-sm uppercase font-semibold mt-1">
                                {currentCamera.direction === 'entry' ? '↓ ENTRY' : '↑ EXIT'}
                              </div>
                            </div>
                          </div>
                          <div className="text-gray-300 text-sm mt-3">
                            {new Date(currentCamera.lastUpdate).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full bg-gray-900">
                      <div className="text-center text-white">
                        <Camera className="w-16 h-16 mx-auto mb-4 opacity-60" />
                        <div className="text-lg">{currentCamera.name}</div>
                      </div>
                    </div>
                  )}
                  
                </div>

                {/* Video Overlay Controls */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-white hover:bg-white/20"
                        onClick={() => setIsRecording(!isRecording)}
                        data-testid="record-button"
                      >
                        {isRecording ? <Square className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-white hover:bg-white/20"
                        onClick={() => setIsMuted(!isMuted)}
                      >
                        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      </Button>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-white hover:bg-white/20"
                        onClick={() => setIsFullscreen(!isFullscreen)}
                      >
                        <Maximize2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Recording Indicator */}
                {isRecording && (
                  <div className="absolute top-4 right-4 flex items-center space-x-2 bg-red-600 px-3 py-1 rounded-full text-white text-sm animate-pulse">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    <span>REC</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
}